import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';

import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { useServiceError } from '@/hooks';
import {
  createSaleItem,
  deleteSaleItem,
  saleItemHistory,
  updateSaleItem,
} from './SaleItem.service';
import {
  SaleItemCreate,
  SaleItemEdit,
  SaleItemHistoryRequest,
} from './SaleItem.types';
import notificationError from '@/utils/functions/notificationError';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';

export function useCreateSaleItem() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation((obj: SaleItemCreate) => createSaleItem(obj), {
    onSuccess() {
      queryClient.invalidateQueries('direct-sale');

      notification.success({
        message: intl.formatMessage({
          id: 'directSale.notification.create.item.success.title',
        }),
        description: intl.formatMessage({
          id: 'directSale.notification.create.item.success.description',
        }),
      });
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Cannot add or remove item of a canceled sale!')
              ? 'Não é possível adicionar item de uma venda cancelada!'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useUpdateSaleItem() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation((obj: SaleItemEdit) => updateSaleItem(obj), {
    onSuccess() {
      queryClient.invalidateQueries('direct-sale');

      notification.success({
        message: intl.formatMessage({
          id: 'directSale.notification.update.item.success.title',
        }),
        description: intl.formatMessage({
          id: 'directSale.notification.update.item.success.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteSaleItem(saleId: number) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (itemId: number) => deleteSaleItem(saleId, itemId),
    {
      onSuccess() {
        queryClient.invalidateQueries(['direct-sale']);
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('Cannot add or remove item of a canceled sale!')
                ? 'Não é possível remover item de uma venda cancelada!'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useSaleItemHistory(
  params: SaleItemHistoryRequest,
  enabled: boolean,
) {
  return useInfiniteQuery(
    ['sale-item-history', { ...params }],
    ({ pageParam }) => {
      const req: SaleItemHistoryRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return saleItemHistory(req);
    },
    {
      enabled,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );
}
